import React, { useState } from "react";
import { useAIAssessmentContext } from "../../../../Context/AIAssessmentContext/AIAssessmentContext";
import { Result, Button, Divider, Typography } from "antd";
import { InterviewSession } from "../../../../Interface/Index";
import AuthService from "../../../../Services/auth.service";
import { AIAssessmentDetails } from "../../../../Entities/ai-assessment.entity";
import AiAssessmentFailureReasonModal from "../modals/AiAssessmentFailureReasonModal";
import { groupActivityLogs } from "../../utils/interview-utils";

// Violation limits (mirror of backend configuration)
const VIOLATION_LIMITS: Record<string, number> = {
  fullscreen_exit: 1,
  keyboard_shortcut: 3,
  cursor_focus_switch: 3,
  multiple_tabs: 3,
  refresh_page: 1,
  screen_recording_stopped: 1,
  fullscreen_exit_attempt: 3,
};

type DisqualificationReason =
  | "session_expired"
  | "missed_pings"
  | "violation_limit"
  | "low_score";

const AiAssessmentResult: React.FC = () => {
  const [isFailureReasonModalOpen, setIsFailureReasonModalOpen] =
    useState(false);

  const currentUser = AuthService.getInstance().getCurrentUser();

  const firstName = currentUser?.firstName
    ? currentUser.firstName.charAt(0).toUpperCase() +
    currentUser.firstName.slice(1)
    : "";
  const lastName = currentUser?.lastName
    ? currentUser.lastName.charAt(0).toUpperCase() +
    currentUser.lastName.slice(1)
    : "";
  const userName = `${firstName} ${lastName}`.trim() || "User";

  const { currentSession, aiAssessmentDetails } = useAIAssessmentContext();

  // Check if there are any violations (activity logs or missed pings)
  const hasViolations = () => {
    if (!currentSession) return false;

    const { activityLogs, missedPings } = currentSession;
    const groupedActivityLogs = groupActivityLogs(activityLogs || []);

    // Check if there are any activity log violations
    const hasActivityLogViolations = groupedActivityLogs.length > 0;

    // Check if there are missed pings
    const hasMissedPings = missedPings && missedPings > 0;

    return hasActivityLogViolations || hasMissedPings;
  };

  // Determine the reason for disqualification
  const getDisqualificationReason = (): DisqualificationReason | null => {
    if (!currentSession || currentSession.status !== "unqualified") return null;

    const { activityLogs, missedPings } = currentSession;

    // 1. Check for session expiry (server-added event - cast to string for comparison)
    const hasSessionExpired = activityLogs?.some(
      (log) => (log.event as string) === "session_expired"
    );
    if (hasSessionExpired) return "session_expired";

    // 2. Check for missed ping limit (server-added event or count >= 3)
    const hasMissedPingEvent = activityLogs?.some(
      (log) => (log.event as string) === "missed_ping_limit_reached"
    );
    if (hasMissedPingEvent || (missedPings && missedPings >= 3)) {
      return "missed_pings";
    }

    // 3. Check if any violation exceeded its limit
    for (const [eventType, limit] of Object.entries(VIOLATION_LIMITS)) {
      const count =
        activityLogs?.filter((log) => log.event === eventType).length || 0;
      if (count >= limit) {
        return "violation_limit";
      }
    }

    // 4. If none of the above, it's due to low score
    return "low_score";
  };

  const getAiAssessmentResult = (
    session: InterviewSession,
    details: AIAssessmentDetails
  ) => {
    const roundName = details.roundName;
    const violationsExist = hasViolations();
    const disqualificationReason = getDisqualificationReason();

    switch (session.status) {
      case "qualified":
        return {
          status: "success",
          title: `Congratulations ${userName}!`,
          subTitle: `You've successfully completed the ${roundName} AI Assessment Round. Your responses have been submitted and the hiring team will review your application. You'll be notified about the next steps in the process.`,
        };
      case "unqualified":
        // Session expired - timeout
        if (disqualificationReason === "session_expired") {
          return {
            status: "error",
            title: "Session Expired",
            subTitle: `Your ${roundName} AI Assessment session has expired. The hiring team will review your overall application and get back to you.`,
          };
        }

        // Disqualified due to critical violations (missed pings or violation limit)
        if (
          disqualificationReason === "missed_pings" ||
          disqualificationReason === "violation_limit"
        ) {
          return {
            status: "error",
            title: "Thank you for your participation!",
            subTitle: `You've completed the ${roundName} AI Assessment Round. The hiring team will review your overall application and get back to you.`,
          };
        }

        // Disqualified due to low score (may or may not have minor violations)
        return {
          status: "error",
          title: "Thank you for your participation!",
          subTitle: violationsExist
            ? `You've completed the ${roundName} AI Assessment Round. While you didn't meet the required criteria for this round, please note that some assessment guidelines were also not followed. The hiring team will review your overall application and get back to you.`
            : `You've completed the ${roundName} AI Assessment Round. While you didn't meet the required criteria for this round, we appreciate the time and effort you've invested. The hiring team will review your overall application and get back to you.`,
        };
      default:
        return {
          status: "warning",
          title: "Assessment in Progress",
          subTitle: `Your ${roundName} AI Assessment Round is being processed. You'll be notified once the evaluation is complete.`,
        };
    }
  };

  const handleViewFailureReason = () => {
    setIsFailureReasonModalOpen(true);
  };

  const handleCloseFailureReasonModal = () => {
    setIsFailureReasonModalOpen(false);
  };

  return (
    <div className="ai-assessment-result-container">
      <div className="ai-assessment-section-title">
        <Typography.Title level={5} style={{ margin: "0px", fontSize: "14px" }}>
          Result
        </Typography.Title>
      </div>
      <Divider style={{ margin: "0px" }} />
      <div style={{ width: "100%", maxWidth: "800px", margin: "0 auto" }}>
        {currentSession && aiAssessmentDetails && (
          <Result
            status={
              getAiAssessmentResult(currentSession, aiAssessmentDetails)
                .status as any
            }
            icon={null}
            title={
              getAiAssessmentResult(currentSession, aiAssessmentDetails).title
            }
            subTitle={
              getAiAssessmentResult(currentSession, aiAssessmentDetails)
                .subTitle
            }
            // extra={
            //   currentSession &&
            //   currentSession?.status === "unqualified" &&
            //   hasViolations() && (
            //     <Button type="text" onClick={handleViewFailureReason}>
            //       <Typography.Text type="danger">View Violations</Typography.Text>
            //     </Button>
            //   )
            // }
          />
        )}
      </div>

      <AiAssessmentFailureReasonModal
        isOpen={isFailureReasonModalOpen}
        onClose={handleCloseFailureReasonModal}
      />
    </div>
  );
};

export default AiAssessmentResult;
